import React from 'react';
import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Escalation, NiwtAgent } from '../types';

const severityConfig = {
  high: {
    icon: AlertTriangle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    badge: 'bg-red-100 text-red-700',
    label: 'High',
  },
  medium: {
    icon: AlertCircle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-700',
    label: 'Medium',
  },
  low: {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-700',
    label: 'Low',
  },
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

interface Props {
  escalations: Escalation[];
  agents?: NiwtAgent[];
}

const EscalationsPanel: React.FC<Props> = ({ escalations, agents = [] }) => {
  const agentMap = Object.fromEntries(agents.map((a) => [a.id, a]));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center gap-1.5 mb-3">
        <AlertTriangle className="w-4 h-4 text-red-500" />
        <h2 className="text-sm font-semibold text-gray-800">
          Escalations ({escalations.length})
        </h2>
      </div>

      {escalations.length === 0 ? (
        <p className="text-xs text-gray-400 py-3 text-center">No active escalations.</p>
      ) : (
        <div className="space-y-2">
          {escalations.map((esc) => {
            const config = severityConfig[esc.severity];
            const Icon = config.icon;
            const agent = agentMap[esc.source_agent_id];
            return (
              <div
                key={esc.id}
                className={`p-3 rounded-lg border ${config.bg} ${config.border}`}
              >
                <div className="flex items-start gap-2">
                  <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${config.text}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className={`text-xs font-semibold ${config.text} leading-snug`}>
                        {esc.title}
                      </p>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${config.badge}`}>
                        {config.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{esc.summary}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {agent ? `via ${agent.display_name}` : ''}
                      {agent ? ' · ' : ''}{relativeTime(esc.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EscalationsPanel;
